import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Smart API URL detection - use relative for external preview, env var for localhost
const getApiUrl = () => {
  // If accessed via preview URL, use relative paths (ingress handles routing)
  if (typeof window !== 'undefined' && window.location.hostname.includes('preview.emergentagent.com')) {
    return '/api';
  }
  // For localhost development, use the backend URL from env
  return `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
};
const API_URL = getApiUrl();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(newUser);
    
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const hasRole = (roles) => {
    if (!user) return false;
    // Support both new roles array and legacy single role
    const userRoles = user.roles || [user.role];
    if (typeof roles === 'string') {
      return userRoles.includes(roles);
    }
    return roles.some(role => userRoles.includes(role));
  };

  // Check if user has ANY of the specified roles (alias for hasRole)
  const hasAnyRole = (roles) => hasRole(roles);
  
  // Check if user has ALL of the specified roles
  const hasAllRoles = (roles) => {
    if (!user) return false;
    const userRoles = user.roles || [user.role];
    if (typeof roles === 'string') return userRoles.includes(roles);
    return roles.every(role => userRoles.includes(role));
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, hasRole, hasAnyRole, hasAllRoles }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
